// Trips area — list page, trip edit page and all trip tab/editor components.
export const trips = {
  list: {
    title: "Trips",
    subtitle: "Manage trip catalog",
    newTrip: "New trip",
    searchPlaceholder: "Search trips...",
    allStatuses: "All statuses",
    loadError: "Error loading trips. Please try again.",
    empty: "No trips found",
    duration: "Duration",
    priceFrom: "from {{price}}",
    days_one: "{{count}} day",
    days_few: "{{count}} days",
    days_other: "{{count}} days",
    confirmDelete: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
    noCoordinates: "No coordinates",
    noCoordinatesHint:
      "The hotel has no coordinates, so its page shows no map. Fill them in on the Hotel tab.",
  },
  order: {
    title: "Trip order",
    subtitle:
      "One manual ranking that drives the default order everywhere the catalogue is listed.",
    openFromList: "Trip order",
    ranked: "Ranking",
    rankedEmpty: "The ranking is empty — add a trip from the list on the right.",
    unranked: "Unranked published trips",
    unrankedEmpty: "No unranked published trip matches your search.",
    unrankedHint:
      "Unranked trips are a normal state — they are listed below the ranking, newest first.",
    searchPlaceholder: "Search trips…",
    add: "Add to ranking",
    remove: "Remove from ranking",
    moveUp: "Move up",
    moveDown: "Move down",
    dragHandle: "Drag to reorder",
    position: "Position {{position}}",
    saving: "Saving order…",
    saveError: "The order could not be saved. Please try again.",
    loadError: "Trips could not be loaded. Please try again.",
    lengthHint:
      "The ranking has {{total}} trips. Around a hundred is the recommended length — longer rankings get hard to maintain.",
  },
  edit: {
    tabs: {
      basic: "Basic info",
      itinerary: "Itinerary",
      experience: "Experience details",
      hotel: "Hotel",
      included: "What's included",
      gallery: "Gallery",
      dates: "Dates",
      components: "Components",
      calculation: "Calculation",
      content: "Content",
    },
    newTrip: "New trip",
    editTrip: "Edit trip",
    newSubtitle: "Create a new trip",
    editingSubtitle: "Editing: {{title}}",
    notFound: "Trip not found",
    backToTrips: "Back to trips",
    statusDraft: "Draft",
    statusPublish: "Publish",
    statusArchive: "Archive",
    hasValidationErrors: "Has validation errors",
    unsavedParts: {
      message:
        "The trip was created, but these parts were not saved: {{parts}}. Enter them again and save the trip.",
      parts: {
        upgrades: "premium upgrades",
        dates: "dates",
        components: "components",
      },
      close: "Close",
    },
  },
  form: {
    sectionBasic: "Basic information",
    titleRequired: "Title is required",
    titlePlaceholder: "e.g. Vietnam - From North to South",
    slug: "URL slug",
    slugRequired: "Slug is required",
    slugPlaceholder: "e.g. vietnam-north-to-south",
    destinationRequired: "Destination is required",
    selectDestination: "Select destination",
    destinationTags: "Additional destinations",
    destinationTagsSearch: "Search destinations...",
    guide: "Guide",
    noGuide: "No guide",
    guideVideo: "Guide video (YouTube URL)",
    difficulty: "Difficulty",
    tripKind: "Trip kind",
    tripKindTour: "Tour (multi-day itinerary)",
    tripKindExperience: "Experience (short highlights)",
    tripKindHint:
      "Content shape: tours show a day-by-day itinerary; experiences show short highlights.",
    bookingMode: "Booking mode",
    bookingModeFixed: "Fixed departures (pick from dates)",
    bookingModeOnRequest: "On request (any-day calendar)",
    bookingModeHint:
      "How customers book: from scheduled departures, or any day on the calendar (honoring available weekdays + lead time). A private departure is created on conversion.",
    tripType: "Trip type",
    countries: "Countries (ISO-2)",
    tripStyle: "Trip style",
    styleNotSet: "— Not set —",
    sectionPricing: "Price and duration",
    priceFrom: "Price (CZK)",
    priceIsFrom: 'Show as "from" price',
    priceIsFromHint:
      'Web shows "from CZK 55,000" instead of a fixed amount — for the trip price and for each departure price. Resort trips are always "from". Varying departure prices trigger it even when unchecked.',
    priceOriginal: "Original price (CZK)",
    durationHours: "Duration (hours)",
    durationDays: "Duration (days)",
    groupSize: "Group size",
    groupSizePeople: "people",
    groupMinAtLeast: "Min must be at least 1",
    groupMinExceedsMax: "Min cannot exceed max",
    groupMaxAtLeast: "Max must be at least 1",
    groupMaxBelowMin: "Max cannot be less than min",
    sectionMeetingPoint: "Default meeting point",
    meetingPoint: "Meeting point",
    meetingPointPlaceholder: "e.g. Hanoi Old Quarter office, 12 Hang Bac",
    meetingTime: "Time",
    heroTitle: "Hero title",
    heading: "Page heading (H1)",
    sectionDescription: "Description",
    shortDescription: "Card teaser & SEO description",
    shortDescriptionPlaceholder: "One-line teaser shown on trip cards",
    fullDescription: "Full description",
    fullDescriptionPlaceholder: "Detailed trip description...",
    sectionSeo: "SEO",
    metaTitle: "Meta title",
    metaTitlePlaceholder: "SEO title (max 60 characters)",
    metaDescription: "Meta description",
    metaDescriptionPlaceholder: "SEO description (max 160 characters)",
    featuredImage: "Featured image",
    showAsFeatured: "Show as featured",
    lastMinuteOffer: "Last minute offer",
    forWomen: "Trip for women",
    hasCzechGuide: "Has Czech guide",
    tags: "Tags",
    region: "Region",
    regionNorth: "North",
    regionCentral: "Central",
    regionSouth: "South",
    otherTags: "Other tags",
    addTagPlaceholder: "Add tag and press Enter",
    contentOverview: "Content overview",
    statItineraryDays: "Days in itinerary:",
    statGalleryImages: "Gallery images:",
    statIncluded: "Included items:",
    statExcluded: "Not included items:",
    statDates: "Dates:",
    statFaq: "FAQ items:",
    statReels: "Social reels:",
    statRouteMap: "Route map:",
    yes: "Yes",
    no: "No",
  },
  basic: {
    groupSizeOpenEnded: "No upper limit (by arrangement)",
    groupSizeOpenEndedNoteOnRequest:
      "The website will say “from 1 person, by arrangement”. The capacity of the private departure is taken from the number of travellers in the order — it is not derived from the group size.",
    groupSizeOpenEndedNoteFixed:
      "The website will say “from 1 person, by arrangement”. You then have to fill in the capacity of every departure manually in the Dates tab — it is not derived from the group size.",
  },
  calculation: {
    saveFirst: "Save the trip first — calculations attach to an existing trip.",
  },
  content: {
    faqTitle: "FAQ",
    faqPrefill: {
      topicGeneral: "General",
      q1: "What size are your groups?",
      a1: "Our groups are typically {{groupMin}}-{{groupMax}} travelers. Small groups ensure personal attention and authentic experiences.",
      a1OpenEnded: "From 1 traveller, by arrangement.",
      q2: "Do you offer upgrades?",
      a2: "Yes! We offer premium upgrades including business class flights, luxury hotel upgrades, and private transfers. Ask our specialists for details.",
      q3: "What's the difficulty level?",
      a3: "This trip is rated as {{difficulty}}. It's suitable for most travelers with a reasonable level of fitness.",
      q4: "Is this trip suitable for solo travelers?",
      a4: "Absolutely! Many of our travelers come solo and leave with new friends. Our small group format creates a welcoming environment for everyone.",
    },
    socialReels: "Social reels (Get inspired)",
    routeMap: "Route map",
    guideRecommendation: "Guide recommendation",
    guideRecommendationPlaceholder: "Write a personal recommendation from the guide...",
    similarTrips: "Similar trips",
    searchTripsPlaceholder: "Search trips...",
    relatedPagesTitle: "Related pages",
    relatedPagesSearch: "Search pages...",
    relatedPagesDraftSuffix: "(draft)",
    relatedPagesNone: "No page exists yet.",
    relatedPagesManage: "Manage pages",
    relatedPagesNoRight: "Pages are created by an admin or an editor.",
    inspirations: {
      title: "Inspirations on the route",
      hint: "On the website: the trip appears in the ‘Trips where you can experience it’ section on the selected inspirations' pages; the trip page does not show the link.",
      pickerLabel: "Selected inspirations",
      searchPlaceholder: "Search inspirations...",
      draftSuffix: "(draft)",
      blockedNew: "Links can be added after the first save.",
      blockedUnpublished: "Inspirations on the route can be assigned once the trip is published.",
      loading: "Loading links...",
    },
    premiumUpgrades: "Premium upgrades",
  },
  experience: {
    leadTime: "Lead time (hours)",
    leadTimeHint: "Minimum hours a customer must request the on-request trip in advance",
    availableWeekdays: "Available weekdays",
    availableWeekdaysHint: "Leave all unchecked = the on-request trip is available every day.",
    weekdays: {
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",
      sun: "Sun",
    },
  },
  gallery: {
    title: "Gallery",
    youtubeTitle: "YouTube videos",
    youtubeDescription: "Add YouTube video URLs for this trip",
    youtubeEmpty: "No videos yet",
    youtubeAdd: "Add video",
  },
  highlights: {
    title: "Highlights",
    hint: "Marketing teasers — what makes this experience worth booking.",
    add: "Add highlight",
    titlePlaceholder: "Title (e.g. Hands-on cooking with a local family)",
    descriptionPlaceholder: "Short description (optional)",
    remove: "Remove highlight",
    empty: "No highlights yet — add at least one to make this experience pop.",
  },
  hotel: {
    needsOnRequest:
      'This trip is in "Fixed departures" mode. The hotel presentation (hotel detail page and the hotel listing) only activates once the booking mode is switched to "On request" — customers pick their own stay window there.',
    stars: "Star rating",
    starsNotSet: "— Not set —",
    starsOption_one: "{{count}} star",
    starsOption_few: "{{count}} stars",
    starsOption_other: "{{count}} stars",
    surchargeUnit: "per stay",
    meals: {
      title: "Meal plans",
      hint: "Tick what the hotel offers and set the surcharge for the whole stay (0 = included).",
    },
    departures: {
      title: "Departure places",
      hint: "Where the customer can fly from. Leave all unticked if the hotel is sold without flights only.",
      anyNote:
        'Customers always also get the "No preference" option — it is not administered here.',
    },
    features: {
      title: "Hotel focus",
      hint: "Tick everything that applies. Customers filter beach holidays on the website by these.",
    },
    location: {
      title: "Location on the map",
      label: "Hotel coordinates",
      placeholder: "-8.79203, 115.22704",
      hint: "On Mapy.cz or in Google Maps, right-click the hotel, copy its coordinates and paste them here. Both formats work: signed (-8.79203, 115.22704) and with compass letters (8.79203S, 115.22704E). The coordinates show on the map on the hotel page and as the hotel's own pin on the trips map. Leave empty and the hotel page has no map; the trips map then shows the hotel only at its destination's point.",
    },
    rooms: {
      title: "Rooms",
      hint: "The room catalogue the customer picks from in the inquiry. Drag to reorder.",
      add: "Add room",
      namePlaceholder: "Room name (e.g. Deluxe with sea view)",
      occupancyPlaceholder: "Occupancy (e.g. 2 adults + 2 children)",
      bedsPlaceholder: "Beds (e.g. king size)",
      sizePlaceholder: "Size (m²)",
      descriptionPlaceholder: "Short description shown in the room card…",
      surcharge: "Surcharge (per stay)",
      surchargeHint: '0 means the room is "included in the price".',
      remove: "Remove room",
      empty: "No rooms yet — a hotel without rooms is valid, the inquiry just won't offer any.",
    },
  },
  included: {
    orderHint:
      "The order is fixed: flights first, then accommodation, then everything else. Write the items in this order — the website lists them exactly as they are.",
    includesTitle: "Price includes ({{n}})",
    excludesTitle: "Price does not include ({{n}})",
    includedPlaceholder: "e.g. Round-trip flights",
    excludedPlaceholder: "e.g. Entry visa",
    noItems: "No items yet",
    optionalActivities: "Optional activities",
    addActivity: "Add activity",
    noActivities: "No optional activities.",
    activityNamePlaceholder: "Hot-air balloon flight",
    activityPricePlaceholder: "approx. 90 USD, paid locally",
    activityDescriptionPlaceholder: "Sunrise flight over Vang Vieng (weather permitting)",
    notePlaceholder: "e.g. Prices are per person based on double occupancy...",
  },
  itinerary: {
    title_one: "Itinerary ({{count}} day)",
    title_few: "Itinerary ({{count}} days)",
    title_other: "Itinerary ({{count}} days)",
    addDay: "Add day",
    empty: "No days in itinerary yet",
    addFirstDay: "Add first day",
    dayTitle: "Day {{day}}",
    dayTitlePlaceholder: "Day {{day}}: Title",
    spanDays: "Days covered by this entry",
    spanDaysHint:
      "More than 1 for a programme written in ranges (\u201cDays 1\u20132: flight\u201d). Following days are renumbered automatically.",
    descriptionPlaceholder: "Detailed description of the day...",
    accommodation: "Accommodation",
    accommodationPlaceholder: "e.g. 4* hotel in city center",
    meals: "Meals",
    mealBreakfast: "Breakfast",
    mealLunch: "Lunch",
    mealDinner: "Dinner",
    highlightsLabel: "Highlights (comma separated)",
    highlightsPlaceholder: "e.g. Temple visit, Traditional lunch",
    guideTip: "Guide tip (optional)",
    guideTipPlaceholder: "Personal tip from the guide for this day...",
  },
  schedule: {
    title: "Schedule",
    hint: "Hour-by-hour timeline of what the customer experiences.",
    addStep: "Add step",
    titlePlaceholder: "Title (e.g. Pickup at hotel)",
    descriptionPlaceholder: "Description (optional)",
    removeStep: "Remove step",
    empty: "No schedule steps yet.",
  },
  tips: {
    title: "Travel tips ({{n}})",
    add: "Add tip",
    empty: "No travel tips yet",
    addFirst: "Add first tip",
    newTip: "New tip...",
    category: "Category",
    titlePlaceholder: "e.g. What to pack",
    content: "Content",
    contentPlaceholder: "Detailed tip description...",
    categories: {
      general: "General",
      packing: "Packing",
      health: "Health",
      money: "Money",
      culture: "Culture",
      safety: "Safety",
    },
  },
  componentsEditor: {
    title: "Components / kusovník ({{n}})",
    info: "These priced components auto-create <strong>draft supplier bookings</strong> when an order is created (<em>Per order</em> — scales by pax) or a departure is added (<em>Per departure</em> — one shared booking). It's a template: changing it never touches bookings already on existing orders.",
    empty: "No components yet — add what this trip is made of.",
    seed: "Seed",
    qty: "Qty",
    removeComponent: "Remove component",
    allAdded: "All catalog components added",
    addComponent: "Add a component…",
  },
  dates: {
    title: "Dates ({{n}})",
    addDate: "Add date",
    empty: "No dates yet",
    addFirstDate: "Add first date",
    occupancyTitle: "Derived from booked vs capacity",
    days_one: "{{count}} day",
    days_few: "{{count}} days",
    days_other: "{{count}} days",
    spots: "{{left}}/{{capacity}} spots",
    velin: "Velín",
    openVelin: "Open departure velín",
    private: "Private",
    privateHint:
      "A private date was created by an on-request order. The website does not offer it and saving the trip does not delete it.",
    departureDate: "Departure date",
    returnDate: "Return date",
    priceAmount: "Price (CZK)",
    capacity: "Capacity",
    capacityRequired:
      "Fill in the capacity — this trip has no upper group-size limit to derive it from.",
    booked: "Booked",
    lifecycle: "Lifecycle",
    guideAssignedElsewhere: "Assigned in the Schedule timeline / departure velín.",
    internalNotes: "Notes (internal)",
    internalNotesPlaceholder: "Internal notes for this date...",
    spotsLeftWarning_one: "Only {{count}} spot left!",
    spotsLeftWarning_few: "Only {{count}} spots left!",
    spotsLeftWarning_other: "Only {{count}} spots left!",
  },
};
